import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { studioApi, type StudioDocument, type StudioPage, type StudioQuery } from './studio-api';
import { documentKind, unwrapTile } from './studio-content';
import { ApiError } from '../../api/client';
import { accountLocale } from '../../i18n';
import { recallStudioLists, rememberStudioList, studioOwner } from './studio-store';

interface StudioListState {
  identity: string;
  requestKey: string;
  /** Чей это ответ: вид списка и фильтры запроса — по ним страница рисует выдачу, которая на экране */
  kind: string;
  query: StudioQuery;
  items: StudioDocument[];
  next?: string | null;
  total?: number;
  available_sorts?: string[];
  format_counts?: Record<string, number>;
  cast_counts?: Record<string, number>;
  error: boolean;
  catalogVersion?: string;
  /** Документ, пришедший вместе с первой страницей (companion): у страницы подборки — её документ */
  companion?: unknown;
  /** Язык профиля, на котором запрошена выдача: в память раздела ложится только выдача текущего языка */
  locale?: string;
}
/** Память списка. Сессия показа — владелец и область страницы (вид, подборка), пока список включён: внутри неё
    новая выдача сменяет прежнюю на месте, а выключение или другая область начинают новую, и чужие строки не показываются.
    Кэш — по ключу, в который входит владелец; при смене владельца он очищается. */
interface StudioListMemory {
  owner: string;
  session: string;
  /** Последний ответ этой сессии: его строки стоят на экране, пока грузится новый ключ */
  last?: StudioListState;
  cache: Map<string, StudioListState>;
}
export interface StudioListOptions {
  /** Область страницы: прежняя выдача держится на экране только внутри неё */
  scope?: string;
  /** Поправить фильтры по ответу первой страницы (subFit эталона): true — фильтры поправлены, этот ответ не показываем, придёт новый */
  fit?: (page: StudioPage, query: StudioQuery) => boolean;
  /** Выдачи переживают уход из раздела (studio-store, по владельцу): новый экземпляр начинает с них — выдача области встаёт
      сразу, первая страница подтверждает её в фоне; выдачи других областей лежат в кэше, как пришедшие в этом экземпляре */
  remember?: boolean;
  /** Документ, нужный вместе с первой страницей (страница подборки: её документ и первая страница — один шаг):
      запрашивается параллельно с ней, выдача встаёт, когда пришли оба, и держит его до следующей первой страницы */
  companion?: (query: StudioQuery, signal: AbortSignal) => Promise<unknown>;
}
/** Курсор и метаданные принадлежат конкретным фильтрам, отменённые ответы не смешиваются с новой выдачей.
    Пока грузится новый ключ, на экране остаётся прежняя выдача этой сессии (у эталона выдача меняется сразу и
    пустой ленты не бывает), а ключ, который уже приходил, показывается из кэша сразу и подтверждается ответом. */
export function useStudioList(
  kind: 'apps' | 'app-collections' | 'search' | 'tools',
  query: StudioQuery,
  enabled = true,
  { scope = '', fit, remember = false, companion }: StudioListOptions = {},
) {
  const owner = studioOwner();
  const identity = JSON.stringify(query),
    key = `${owner}:${kind}:${identity}`;
  const [revision, retry] = useState(0);
  const [cursorState, setCursor] = useState<{ identity: string; value?: string }>({ identity: key });
  // Сброс при смене ключа выполняется до commit, поэтому старый курсор не уходит даже в первый запрос новых фильтров.
  if (cursorState.identity !== key) setCursor({ identity: key });
  const cursor = cursorState.identity === key ? cursorState.value : undefined;
  const requestKey = JSON.stringify([key, cursor ?? null, revision]);
  const session = enabled ? `on:${scope}` : '';
  const [memory, setMemory] = useState<StudioListMemory>(() => {
    // память раздела — только своего владельца (studio-store стирает чужую); без владельца её нет
    const kept = remember && owner ? recallStudioLists<StudioListState>() : new Map<string, StudioListState>();
    return {
      owner,
      session,
      last: session ? kept.get(session) : undefined,
      cache: new Map([...kept.values()].map(state => [state.identity, state])),
    };
  });
  // Новая сессия показа — до commit, как и сброс курсора: строки прежней области не успевают встать на экран
  if (memory.session !== session || memory.owner !== owner)
    setMemory(previous => ({ owner, session, cache: previous.owner === owner ? previous.cache : new Map() }));
  const fitter = useRef(fit);
  const companionLoad = useRef(companion);
  // В том же commit, что и новое состояние: ответ, пришедший сразу после него, не правит фильтры прежним fit
  useLayoutEffect(() => {
    fitter.current = fit;
    companionLoad.current = companion;
  });
  /* выдача на экране — в память раздела: возврат в «Студию» встанет с неё (тот же предел, что у кэша). Сбой не помнится:
     возврат подтвердит выдачу заново, а не встанет со сцены ошибки */
  useEffect(() => {
    const last = memory.last;
    if (
      remember &&
      memory.owner &&
      memory.owner === studioOwner() &&
      memory.session &&
      last &&
      !last.error &&
      last.locale === accountLocale() &&
      last.items.length <= 200
    )
      rememberStudioList(memory.session, last);
  }, [remember, memory]);
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const parsed = JSON.parse(identity) as StudioQuery;
    const locale = accountLocale();
    const timeout = setTimeout(
      () => {
        // документ — только с первой страницей: следующие страницы его не меняют
        const load = cursor ? undefined : companionLoad.current;
        void Promise.all([studioApi.list(kind, { ...parsed, cursor }, controller.signal), load?.(parsed, controller.signal)])
          .then(([page, extra]) => {
            if (controller.signal.aborted) return;
            if (!cursor && fitter.current?.(page, parsed)) return;
            setMemory(memo => {
              const previous = memo.last;
              const version =
                typeof page.catalog_version === 'number' || typeof page.catalog_version === 'string'
                  ? String(page.catalog_version).trim()
                  : undefined;
              const saved = owner && parsed.sort !== 'pop' ? memo.cache.get(key) : undefined;
              // После возврата сначала подтверждаем первую страницу. Хвост и его курсор годятся только для той же версии каталога.
              const restore = !cursor && !!version && saved?.catalogVersion === version;
              const compatiblePage = !previous?.catalogVersion || !version || previous.catalogVersion === version;
              const fresh = page.items.map(unwrapTile);
              const items =
                cursor && previous?.identity === key && compatiblePage
                  ? [...previous.items, ...fresh]
                  : restore
                    ? [...fresh, ...saved.items]
                    : fresh;
              const seen = new Set<string>();
              const nextState: StudioListState = {
                identity: key,
                requestKey,
                kind,
                query: parsed,
                items: items.filter(item => {
                  const kind = (item.document_kind ?? item.kind) === 'reel_template' ? 'reel_template' : documentKind(item);
                  const id = `${kind}:${item.id}`;
                  if (seen.has(id)) return false;
                  seen.add(id);
                  return true;
                }),
                next: restore ? saved.next : page.next_cursor,
                catalogVersion: version,
                total: page.total,
                available_sorts: Array.isArray(page.available_sorts)
                  ? page.available_sorts.filter((item): item is string => typeof item === 'string')
                  : undefined,
                format_counts: page.format_counts as Record<string, number> | undefined,
                cast_counts: page.cast_counts as Record<string, number> | undefined,
                error: false,
                companion: load ? extra : cursor && previous?.identity === key ? previous.companion : undefined,
                locale,
              };
              // Кэш живёт только в экземпляре hook и ограничен восемью выдачами; прежний объект не меняется — обновление чистое.
              const cache = new Map(memo.cache);
              cache.delete(key);
              if (owner && nextState.items.length <= 200) {
                cache.set(key, nextState);
                if (cache.size > 8) cache.delete(cache.keys().next().value!);
              }
              return { ...memo, last: nextState, cache };
            });
          })
          .catch(failure => {
            if (controller.signal.aborted) return;
            if (cursor && failure instanceof ApiError && (failure.code === 'catalog_changed' || failure.code === 'cursor_invalid')) {
              // Устаревший курсор больше не повторяем: новая первая страница заменит весь хвост выдачи.
              // До её прихода на экране остаются прежние строки — пустой ленты нет. Выдача на экране ждёт (ключ
              // упавшего запроса — не ключ новой первой страницы) и курсора у неё нет: догрузка не шлёт старый ещё раз.
              setMemory(memo => {
                const cache = new Map(memo.cache);
                cache.delete(key);
                return { ...memo, cache, last: memo.last?.identity === key ? { ...memo.last, requestKey, next: null } : memo.last };
              });
              setCursor({ identity: key });
              return;
            }
            setMemory(memo => ({
              ...memo,
              last: {
                ...(memo.last?.identity === key ? memo.last : { items: [] }),
                identity: key,
                requestKey,
                kind,
                query: parsed,
                error: true,
              },
            }));
          });
      },
      parsed.q ? 250 : 0,
    );
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [identity, kind, key, cursor, enabled, revision, requestKey, owner]);
  const sameSession = memory.session === session && memory.owner === owner;
  const last = sameSession ? memory.last : undefined;
  const current = last?.identity === key ? last : undefined;
  /* Ключ, который уже приходил (возврат на вкладку, прежний запрос): его выдача встаёт сразу, ответ её подтверждает.
     Как и восстановление хвоста, только у своего владельца и не для «Популярных». */
  const cached = !current && sameSession && owner && query.sort !== 'pop' ? memory.cache.get(key) : undefined;
  // прежняя выдача держится, только если она была: ошибка другого ключа пустой сценой не встаёт
  const shown = enabled ? (current ?? cached ?? (last?.error ? undefined : last)) : undefined;
  const loading = enabled && last?.requestKey !== requestKey;
  return {
    identity: key,
    items: shown?.items ?? [],
    next: shown?.next,
    total: shown?.total,
    available_sorts: shown?.available_sorts,
    format_counts: shown?.format_counts,
    cast_counts: shown?.cast_counts,
    /** Документ, пришедший с первой страницей выдачи, что на экране (companion) */
    companion: shown?.companion,
    /** На экране есть выдача этой сессии: ответ этого ключа, его выдача из кэша или прежняя, пока грузится новая */
    ready: !!shown,
    /** Вид и фильтры выдачи, которая на экране: пока грузится новая, они прежние */
    shownKind: shown?.kind,
    shownQuery: shown?.query,
    loading,
    error: !!current && current.requestKey === requestKey && current.error,
    /** На экране — сбой этого ключа (первая страница или следующая не пришла); держится и пока идёт повтор (reload) */
    failed: !!shown?.error,
    reload: () => retry(value => value + 1),
    more: () => {
      if (current && !loading && current.next) setCursor({ identity: key, value: current.next });
    },
  };
}
